import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../cn.js';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Marks the field invalid and switches border/focus colour to `--danger`. Pair with `Field`'s
   *  `error` prop and set `aria-describedby` to the matching error id yourself — this component
   *  does not know the id. */
  invalid?: boolean;
}

/**
 * An unlabeled filter-bar `<select>` — the pattern every list page's Status/Department/Source
 * dropdown already used, just inconsistently: a bare native `<select>` with no unified styling
 * renders with the OS's own chrome (a plain border, no matching chevron, no shared focus ring),
 * which is what made every filter bar across the app look visually disconnected from everything
 * around it. `appearance-none` strips that chrome so the token-driven border/height/focus state
 * below is what actually shows, and the overlaid `ChevronDown` replaces the OS arrow it removed.
 * Still a real `<select>`, not a custom listbox — full native keyboard/screen-reader/mobile support,
 * just restyled.
 *
 * For a LABELED select (a form field, not a filter), compose your own `<label>` around this the
 * same way `Input` does — this component intentionally has no label slot, since every current
 * caller is a filter-bar dropdown identified by its placeholder option instead. `invalid` is still
 * available for a labeled caller that wants the same danger-tone treatment `Input`/`Textarea` give.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid = false, className, children, disabled, ...rest },
  ref,
) {
  return (
    // The select fills its wrapper, so the chevron below sits at the select's own edge: inside a
    // Field (a flex column) the wrapper stretches to the field width and the select with it, and a
    // caller's width class (`w-44`) still sizes it in a filter row. `bg-none` turns off the arrow
    // the app's global `select` rule paints as a background, which drew a second chevron.
    <span className="relative inline-flex min-w-0">
      <select
        ref={ref}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-(--control-height) w-full min-w-0 appearance-none rounded-md border bg-surface bg-none pl-3 pr-8 text-sm text-fg',
          'transition-colors duration-(--duration-fast)',
          'hover:border-border-strong',
          'focus-visible:outline-2 focus-visible:outline-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          invalid
            ? 'border-danger focus-visible:outline-danger'
            : 'border-border focus-visible:outline-accent',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-muted"
      />
    </span>
  );
});
